import { request } from "@frontend/lib/http";

import type {
  AddressCity,
  AddressCountry,
  AddressState,
  AddressStateRow,
  AddressSuggestion,
} from "../types";

type RequestOptions = {
  signal?: AbortSignal;
};

type SearchFilter = {
  q?: string;
  countryIso2?: string;
};

type SearchResults<TRow> = {
  results: TRow[];
};

export type AddressApi = {
  fetchCountries(options?: RequestOptions): Promise<AddressCountry[]>;
  fetchStates(
    countryIso2: string,
    options?: RequestOptions,
  ): Promise<AddressState[]>;
  searchCities(
    filter: SearchFilter,
    options?: RequestOptions,
  ): Promise<AddressCity[]>;
  searchAddresses(
    filter: SearchFilter,
    options?: RequestOptions,
  ): Promise<AddressSuggestion[]>;
};

export const addressApi: AddressApi = {
  fetchCountries(options) {
    return request({ route: "api_referential_countries_list", ...options });
  },

  async fetchStates(countryIso2, options) {
    const rows = await request<AddressStateRow[]>({
      route: "api_referential_states_list",
      query: { countryISO: countryIso2 },
      ...options,
    });

    return namedStates(rows);
  },

  async searchCities(filter, options) {
    const { results } = await request<SearchResults<AddressCity>>({
      route: "api_search_referential_cities_filter",
      method: "POST",
      body: { filter },
      ...options,
    });

    return results;
  },

  async searchAddresses(filter, options) {
    const { results } = await request<SearchResults<AddressSuggestion>>({
      route: "api_search_referential_addresses_filter",
      method: "POST",
      body: { filter },
      ...options,
    });

    return results;
  },
};

/**
 * The referential holds Switzerland twice, once as `CH-VD` named `Vaud` and
 * once as `VD` named `VD`. Both mean the canton an address writes as `VD`, so
 * the two collapse into one row, keeping the one that reads as a name.
 */
function namedStates(rows: AddressStateRow[]): AddressState[] {
  const byCode = new Map<string, AddressState>();

  for (const row of rows) {
    const code = bareCode(row);

    if ("" === code) {
      continue;
    }

    const known = byCode.get(code);

    if (undefined === known || known.name === known.code) {
      byCode.set(code, { id: row.id, name: row.name, code });
    }
  }

  return [...byCode.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** `CH-VD` and `VD` are the same canton; an address stores the second. */
function bareCode(row: AddressStateRow): string {
  const code = row.stateCode ?? row.name;

  const prefix = `${row.countryIso2 ?? ""}-`;

  return code.startsWith(prefix) && "-" !== prefix
    ? code.slice(prefix.length)
    : code;
}
